import React, { useState, useEffect, useCallback } from 'react';
import ComplianceBadges from '../components/ComplianceBadges';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface Procedure {
  name: string;
  role: string;
  estimated_minutes: number;
  requires_fasting: boolean;
  sample_type: string | null;
}

interface Visit {
  visit_name: string;
  day_number: number;
  week_number: number;
  procedures: Procedure[];
  estimated_duration_minutes: number;
  assigned_roles: string[];
}

interface ScheduleResponse {
  protocol_id: string;
  trial_name: string;
  visits: Visit[];
  total_procedures: number;
  total_visits: number;
  estimated_trial_duration_weeks: number;
}

type ProtocolId = 'sym004-09' | 'herald';

const ROLE_ICONS: Record<string, string> = {
  nurse: '\ud83d\udc89',
  PI: '\ud83e\ude7a',
  coordinator: '\ud83d\udccb',
  lab: '\ud83e\uddea',
  pharmacist: '\ud83d\udc8a',
};

function durationColor(minutes: number): string {
  if (minutes < 60) return '#48bb78';
  if (minutes <= 120) return '#ecc94b';
  return '#f56565';
}

function uniqueRoles(visits: Visit[]): string[] {
  const set = new Set<string>();
  visits.forEach((v) => v.assigned_roles.forEach((r) => set.add(r)));
  return Array.from(set);
}

export default function VisitTimeline() {
  const [protocol, setProtocol] = useState<ProtocolId>('sym004-09');
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<string | null>(null);

  const fetchSchedule = useCallback(async (pid: ProtocolId) => {
    setLoading(true);
    setError(null);
    setSelectedVisit(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/schedule/builtin/${pid}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ScheduleResponse = await res.json();
      setSchedule(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load schedule');
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedule(protocol);
  }, [protocol, fetchSchedule]);

  const roles = schedule ? uniqueRoles(schedule.visits) : [];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <h1 style={{ color: '#e2e8f0', fontSize: 22, fontWeight: 700, margin: 0 }}>
        Visit Schedule Timeline
      </h1>
      <p style={{ color: '#a0aec0', fontSize: 14, marginTop: 4, marginBottom: 20 }}>
        Coordinator view &mdash; extracted from protocol
      </p>

      {/* Protocol selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center' }}>
        <span style={{ color: '#a0aec0', fontSize: 13, marginRight: 4 }}>Protocol:</span>
        {(['sym004-09', 'herald'] as ProtocolId[]).map((pid) => (
          <button
            key={pid}
            onClick={() => setProtocol(pid)}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              border: `1px solid ${protocol === pid ? '#4299e1' : '#2d3748'}`,
              backgroundColor: protocol === pid ? 'rgba(66, 153, 225, 0.15)' : '#1a202c',
              color: protocol === pid ? '#63b3ed' : '#a0aec0',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: protocol === pid ? 600 : 400,
            }}
          >
            {pid === 'sym004-09' ? 'Sym004-09' : 'HERALD'}
          </button>
        ))}
      </div>

      {/* Loading / Error */}
      {loading && (
        <div style={{ color: '#a0aec0', fontSize: 14, padding: 20 }}>Loading schedule...</div>
      )}
      {error && (
        <div style={{ color: '#f56565', fontSize: 14, padding: 20 }}>Error: {error}</div>
      )}

      {schedule && !loading && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
            {[
              { label: 'Total Visits', value: schedule.total_visits, color: '#4299e1' },
              { label: 'Total Procedures', value: schedule.total_procedures, color: '#9f7aea' },
              { label: 'Trial Duration', value: `${schedule.estimated_trial_duration_weeks} wks`, color: '#48bb78' },
              { label: 'Roles Involved', value: roles.join(', '), color: '#ecc94b' },
            ].map((card) => (
              <div
                key={card.label}
                style={{
                  backgroundColor: '#1a202c',
                  border: '1px solid #2d3748',
                  borderRadius: 8,
                  padding: '14px 16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ color: '#718096', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  {card.label}
                </div>
                <div style={{ color: card.color, fontSize: 20, fontWeight: 700 }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* Horizontal timeline */}
          <div
            style={{
              overflowX: 'auto',
              marginBottom: 28,
              padding: '12px 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                minWidth: schedule.visits.length * 110,
                padding: '30px 20px 40px 20px',
              }}
            >
              {/* Connecting line */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 20,
                  right: 20,
                  height: 2,
                  backgroundColor: '#2d3748',
                  transform: 'translateY(-50%)',
                }}
              />

              {schedule.visits.map((v, idx) => {
                const col = durationColor(v.estimated_duration_minutes);
                const isSelected = selectedVisit === v.visit_name;
                return (
                  <div
                    key={v.visit_name}
                    onClick={() => setSelectedVisit(isSelected ? null : v.visit_name)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      cursor: 'pointer',
                      zIndex: 1,
                      minWidth: 90,
                    }}
                  >
                    {/* Day label above */}
                    <div
                      style={{
                        color: '#718096',
                        fontSize: 10,
                        marginBottom: 6,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Day {v.day_number}
                    </div>

                    {/* Circle node */}
                    <div
                      style={{
                        width: isSelected ? 22 : 16,
                        height: isSelected ? 22 : 16,
                        borderRadius: '50%',
                        backgroundColor: col,
                        border: isSelected ? '3px solid #e2e8f0' : '2px solid #0d1117',
                        boxShadow: isSelected ? `0 0 10px ${col}` : 'none',
                        transition: 'all 0.15s ease',
                      }}
                    />

                    {/* Visit name below */}
                    <div
                      style={{
                        color: isSelected ? '#e2e8f0' : '#a0aec0',
                        fontSize: 10,
                        marginTop: 6,
                        textAlign: 'center',
                        fontWeight: isSelected ? 600 : 400,
                        whiteSpace: 'nowrap',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {v.visit_name}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 4 }}>
              {[
                { label: '< 60 min', color: '#48bb78' },
                { label: '60-120 min', color: '#ecc94b' },
                { label: '> 120 min', color: '#f56565' },
              ].map((l) => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: l.color }} />
                  <span style={{ color: '#718096', fontSize: 11 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Visit detail cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {schedule.visits
              .filter((v) => !selectedVisit || v.visit_name === selectedVisit)
              .map((v) => {
                const col = durationColor(v.estimated_duration_minutes);
                const hasFasting = v.procedures.some((p) => p.requires_fasting);
                const sampleTypes = [
                  ...new Set(v.procedures.map((p) => p.sample_type).filter(Boolean)),
                ];
                return (
                  <div
                    key={v.visit_name}
                    style={{
                      backgroundColor: '#1a202c',
                      border: '1px solid #2d3748',
                      borderRadius: 8,
                      padding: '16px 20px',
                    }}
                  >
                    {/* Card header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 700 }}>
                          {v.visit_name}
                        </div>
                        <div style={{ color: '#718096', fontSize: 11 }}>
                          Day {v.day_number} &middot; Week {v.week_number}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: '3px 10px',
                          borderRadius: 12,
                          backgroundColor: `${col}22`,
                          color: col,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {v.estimated_duration_minutes} min
                      </div>
                    </div>

                    {/* Procedures */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {v.procedures.map((p, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 8px',
                            backgroundColor: 'rgba(13, 17, 23, 0.4)',
                            borderRadius: 4,
                          }}
                        >
                          <span style={{ fontSize: 13 }}>
                            {ROLE_ICONS[p.role] || '\u2022'}
                          </span>
                          <span style={{ color: '#a0aec0', fontSize: 12, flex: 1 }}>
                            {p.name}
                          </span>
                          <span style={{ color: '#718096', fontSize: 10 }}>
                            {p.estimated_minutes}m &middot; {p.role}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Badges row */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      {hasFasting && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 10,
                            backgroundColor: 'rgba(236, 201, 75, 0.15)',
                            color: '#ecc94b',
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        >
                          Fasting Required
                        </span>
                      )}
                      {sampleTypes.map((st) => (
                        <span
                          key={st as string}
                          style={{
                            padding: '2px 8px',
                            borderRadius: 10,
                            backgroundColor: 'rgba(159, 122, 234, 0.15)',
                            color: '#9f7aea',
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        >
                          {st}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Footer note */}
          <div
            style={{
              marginTop: 28,
              backgroundColor: '#1a202c',
              border: '1px solid #2d3748',
              borderRadius: 8,
              padding: '14px 20px',
            }}
          >
            <p style={{ color: '#718096', fontSize: 13, margin: 0, lineHeight: 1.6 }}>
              <span style={{ color: '#48bb78', fontWeight: 600 }}>
                Replaces the Excel tracker coordinators currently build manually.
              </span>{' '}
              Visit schedules are extracted directly from the protocol and kept in sync with
              the Schedule of Assessments.
            </p>
          </div>

          {/* Compliance badges */}
          <div style={{ marginTop: 16 }}>
            <ComplianceBadges badges={['ICH E6(R3)', '21 CFR Part 11', 'ALCOA']} />
          </div>
        </>
      )}
    </div>
  );
}
